/**
 * SDLC Manager landing — /app/landing/sdlc
 *
 * Software delivery lifecycle: application owners, release managers,
 * dev managers. Hero tiles surface the four DORA metrics, plus open
 * postmortems, ChangeProposals awaiting approval, and failing CI count.
 * Two-column body shows recent decisions/postmortems on the left and
 * drift / service-readme gaps on the right.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  GitMerge, GitPullRequest, FileText, Activity,
} from 'lucide-react'
import { api } from '../lib/api'
import {
  LandingHeader, HeroTile, TwoColumnBody, Panel, EmptyState,
  StickyFooter, ListRow, safeNum,
} from '../components/landing/LandingShell'

interface DoraMetrics {
  deploy_frequency_per_day?: number
  lead_time_hours?: number
  mttr_minutes?: number
  change_failure_rate?: number
}
interface Postmortem { id: string; title?: string; status?: string; created_at?: string }
interface Decision { id: string; title?: string; created_at?: string; status?: string }
interface DriftFinding { id: string; resource?: string; severity?: string; message?: string }
interface ServiceHealth { id: string; name?: string; readme_score?: number; gaps?: string[] }

export default function SdlcLandingPage() {
  const [refreshing, setRefreshing]     = useState(false)
  const [dora, setDora]                 = useState<DoraMetrics | null>(null)
  const [postmortems, setPostmortems]   = useState<Postmortem[]>([])
  const [decisions, setDecisions]       = useState<Decision[]>([])
  const [drift, setDrift]               = useState<DriftFinding[]>([])
  const [services, setServices]         = useState<ServiceHealth[]>([])
  const [proposals, setProposals]       = useState<number>(0)
  const [failingCi, setFailingCi]       = useState<number>(0)

  const load = useCallback(() => {
    setRefreshing(true)
    const j = (p: Promise<any>) => p.catch(() => ({ data: null }))
    Promise.all([
      j(api.get('/api/eng/dora', { params: { window_days: 90 } })),
      j(api.get('/api/postmortems', { params: { state: 'open', limit: 5 } })),
      j(api.get('/api/decisions', { params: { limit: 5 } })),
      j(api.get('/api/sre/drift', { params: { limit: 5 } })),
      j(api.get('/api/services/health', { params: { limit: 50 } })),
      j(api.get('/api/cdm/changes/proposals', { params: { state: 'awaiting_approval' } })),
      j(api.get('/api/ci/status', { params: { state: 'failing' } }).catch(() => ({ data: null }))),
    ]).then(([d, p, dec, dr, s, c, ci]) => {
      setDora(d.data || null)
      setPostmortems(p.data?.postmortems || p.data?.results || p.data?.items || [])
      setDecisions(dec.data?.decisions || dec.data?.results || [])
      setDrift(dr.data?.findings || dr.data?.results || [])
      const svcs = (s.data?.services || s.data?.results || []) as ServiceHealth[]
      setServices(svcs)
      setProposals(c.data?.proposals?.length ?? c.data?.count ?? 0)
      setFailingCi(ci.data?.failing ?? ci.data?.count ?? (Array.isArray(ci.data?.builds) ? ci.data.builds.length : 0))
    }).finally(() => setRefreshing(false))
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  const cfr = dora?.change_failure_rate
  const cfrPct = cfr != null ? (cfr <= 1 ? cfr * 100 : cfr) : null
  const lt = dora?.lead_time_hours
  const mttrM = dora?.mttr_minutes
  const readmeGaps = services.filter(s => (s.readme_score ?? 100) < 70).length

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      <LandingHeader
        title="SDLC Landing"
        subtitle="DORA · postmortems · drift · service-readme · changes"
        persona="sdlc"
        onRefresh={load}
        refreshing={refreshing}
      />

      {/* Hero — 7 tiles laid out 2/3/6 → 4 small + 3 wider on lg */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroTile label="Deploy freq"
          value={dora?.deploy_frequency_per_day != null ? `${dora.deploy_frequency_per_day.toFixed(1)}/d` : '—'}
          tone={dora?.deploy_frequency_per_day == null ? 'muted'
                : dora.deploy_frequency_per_day >= 1 ? 'emerald'
                : dora.deploy_frequency_per_day >= 0.14 ? 'cyan' : 'amber'}
          to="/app/dora" />
        <HeroTile label="Lead time"
          value={lt != null ? (lt < 24 ? `${lt.toFixed(1)}h` : `${(lt / 24).toFixed(1)}d`) : '—'}
          tone={lt == null ? 'muted' : lt <= 24 ? 'emerald' : lt <= 168 ? 'cyan' : 'amber'}
          to="/app/dora" />
        <HeroTile label="MTTR"
          value={mttrM != null ? (mttrM < 60 ? `${mttrM.toFixed(0)}m` : `${(mttrM / 60).toFixed(1)}h`) : '—'}
          tone={mttrM == null ? 'muted' : mttrM <= 60 ? 'emerald' : mttrM <= 240 ? 'cyan' : 'amber'}
          to="/app/dora" />
        <HeroTile label="Change-fail rate"
          value={cfrPct != null ? `${cfrPct.toFixed(0)}%` : '—'}
          tone={cfrPct == null ? 'muted' : cfrPct <= 15 ? 'emerald' : cfrPct <= 30 ? 'amber' : 'rose'}
          to="/app/dora" />
        <HeroTile label="Open postmortems" value={safeNum(postmortems.length)}
          tone={postmortems.length > 0 ? 'amber' : 'emerald'} to="/app/postmortems" />
        <HeroTile label="Awaiting approval" value={safeNum(proposals)}
          tone={proposals > 0 ? 'amber' : 'emerald'} to="/app/cdm/changes" />
      </div>

      {/* failing-CI doesn't fit the 6-column row cleanly; show inline */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <HeroTile label="Failing CI" value={safeNum(failingCi)}
          tone={failingCi > 0 ? 'rose' : 'emerald'} to="/app/ci" />
        <HeroTile label="README gaps" value={safeNum(readmeGaps)}
          tone={readmeGaps > 0 ? 'amber' : 'emerald'} to="/app/service-readmes" />
        <HeroTile label="Drift findings" value={safeNum(drift.length)}
          tone={drift.length > 0 ? 'amber' : 'emerald'} to="/app/drift" />
      </div>

      <TwoColumnBody
        left={(
          <>
            <Panel title="Recent postmortems" viewAllTo="/app/postmortems">
              {postmortems.length === 0 ? (
                <EmptyState message="No open postmortems."
                  configureTo="/app/postmortems" configureLabel="open postmortems" />
              ) : (
                <div className="space-y-1">
                  {postmortems.slice(0, 5).map(p => (
                    <ListRow key={p.id}
                      to="/app/postmortems"
                      primary={p.title || p.id}
                      secondary={p.created_at?.slice(0, 16)}
                      right={p.status} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Recent ADRs / decisions" viewAllTo="/app/decisions">
              {decisions.length === 0 ? (
                <EmptyState message="No decisions captured yet."
                  configureTo="/app/decisions" configureLabel="open decisions log" />
              ) : (
                <div className="space-y-1">
                  {decisions.slice(0, 5).map(d => (
                    <ListRow key={d.id}
                      to="/app/decisions"
                      primary={d.title || d.id}
                      secondary={d.created_at?.slice(0, 16)}
                      right={d.status} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
        right={(
          <>
            <Panel title="Drift findings" viewAllTo="/app/drift">
              {drift.length === 0 ? (
                <EmptyState message="No drift findings."
                  configureTo="/app/drift" configureLabel="run drift scan" />
              ) : (
                <div className="space-y-1">
                  {drift.slice(0, 5).map(d => (
                    <ListRow key={d.id}
                      primary={d.resource || d.id}
                      secondary={d.message}
                      right={<span className={d.severity === 'critical' ? 'text-rose-light' : 'text-amber-light'}>
                        {d.severity || 'finding'}
                      </span>} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Service-readme gaps" viewAllTo="/app/service-readmes">
              {readmeGaps === 0 ? (
                <EmptyState message="All services have healthy READMEs." />
              ) : (
                <div className="space-y-1">
                  {services.filter(s => (s.readme_score ?? 100) < 70).slice(0, 5).map(s => (
                    <ListRow key={s.id}
                      primary={s.name || s.id}
                      secondary={s.gaps?.join(', ')}
                      right={<span className="text-amber-light">{s.readme_score ?? 0}/100</span>} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
      />

      <StickyFooter actions={[
        { label: 'DORA metrics',      icon: GitMerge,        tone: 'primary',   to: '/app/dora' },
        { label: 'Service graph',     icon: Activity,        tone: 'secondary', to: '/app/service-graph' },
        { label: 'Postmortems',       icon: FileText,        tone: 'ghost',     to: '/app/postmortems' },
        { label: 'Change pipeline',   icon: GitPullRequest,  tone: 'ghost',     to: '/app/cdm/changes' },
      ]} />
    </div>
  )
}
